/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ResetObject } from '../components/accounts/reset-password.component';
import { PasswordChangeObject } from '../components/accounts/change-password.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BasicAlertComponent } from '../components/alerts/basic-alert.component';
import { ErrorService } from '../services/error.service';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  baseUrl = 'api/account/';

  constructor(
    private http: HttpClient,
    private modalService: NgbModal,
    private errorService: ErrorService,
  ) {}

  async sendPasswordResetEmail(email: string) {
    return await this.http
      .get(`${this.baseUrl}reset-password-email`, { params: { email: email } })
      .toPromise();
  }

  async validateResetLink(email: string, type: string, resetDate: string) {
    return await this.http
      .get(`${this.baseUrl}validate-reset-link`, {
        params: { email: email, type: type, resetDate: resetDate },
      })
      .toPromise();
  }

  async resetPassword(resetObj: ResetObject) {
    return await this.http
      .post(`${this.baseUrl}reset-password`, resetObj)
      .toPromise();
  }

  async changePassword(passwordChangeObj: PasswordChangeObject) {
    return await this.http
      .post(`${this.baseUrl}change-password`, passwordChangeObj)
      .toPromise();
  }

  getNeededTos(email: string) {
    return this.http.get(`${this.baseUrl}get-needed-tos`, {
      params: { email: email },
    });
  }

  acceptNeededTos(email: string, neededTos: string[]) {
    return this.http.post(`${this.baseUrl}accept-needed-tos`, neededTos, {
      params: { email: email },
    });
  }

  async inviteUser(
    email: string,
    appId: string,
    language: string,
    prefillCompany: string,
  ) {
    return await this.http
      .get(`${this.baseUrl}invite-user`, {
        params: {
          email: email,
          appId: appId,
          language: language,
          prefillCompany: prefillCompany,
        },
      })
      .toPromise();
  }

  async resendInvite(userId: string) {
    return await this.http
      .get(`${this.baseUrl}invite-resend-user`, { params: { userId: userId } })
      .toPromise();
  }

  async forgotPassword(email: string) {
    try {
      await this.sendPasswordResetEmail(email);
      this.modalService.dismissAll();
      const modalRef = this.modalService.open(BasicAlertComponent);
      modalRef.componentInstance.title = 'SDE_PASSWORD_RESET_INSTRUCTIONS1';
      modalRef.componentInstance.body = 'SDE_IF_DO_NOT8';
      modalRef.componentInstance.buttonText = 'SDE_CLOSE';
    } catch (e) {
      try {
        interface CatchError {
          error: string;
        }
        if ((e as CatchError).error !== undefined) {
          const err = e as CatchError;
          const obj = JSON.parse(err.error);
          switch (obj.message) {
            case 'userSuspended':
              return 'SDE_ACCOUNT_SUSPENDED';
            case 'emailOrdomainBlackListed':
              return 'SDE_EMAIL_ADDRESS_CANNOT3';
            case 'invited':
              return 'SDE_ACCOUNT_NOT_ACTIVATED';
            default:
              return 'SDE_EMAIL_NOT_FOUND';
          }
        }
      } catch (error) {
        this.errorService.displayGeneralError().result.then(() => {
          this.modalService.dismissAll();
        });
        console.log(error);
      }
    }
    return 'SDE_EMAIL_NOT_FOUND';
  }
}
